import { z } from 'zod';
import { Chips, GameId, StakeTier, TableTalkId } from './domain.js';
/**
 * Socket protocol. The client is presentation only (PRD §12.1) — it never
 * sends state, only intents, and it never receives another seat's private
 * data (PRD §12.4, and Gera especially per §5).
 */
export const SeatIndex = z.number().int().min(0).max(3);
/**
 * Close code for "another client took this account's socket".
 *
 * In the private 4000–4999 range, and it exists so the two sides can tell a
 * takeover from a dropped signal. Without it the loser reconnects, its hello
 * evicts the winner, and two clients on one account flap forever — measured at
 * about 1.4 seconds a cycle, with both of them reporting lost signal on a
 * perfectly good network.
 */
export const WS_TAKEN_OVER = 4001;
/** ── Client → server ─────────────────────────────────────────────────── */
export const CHello = z.object({ t: z.literal('hello'), token: z.string().min(1) });
export const CPing = z.object({ t: z.literal('ping'), ts: z.number() });
export const CQueueJoin = z.object({
    t: z.literal('queue.join'),
    game: GameId,
    tier: StakeTier,
    /** Buy-in chosen inside the tier band; server re-validates. */
    buyIn: Chips,
});
export const CQueueLeave = z.object({ t: z.literal('queue.leave') });
/**
 * Sit down alone against the house bots — PRD §8.1's "vs AI solo table".
 *
 * Deliberately not a flag on `queue.join`: there is no queue. The table is
 * built and filled in one go, so no `queue.update` ever follows and nothing
 * about waiting applies. It is an ordinary online table in every other respect
 * — real buy-in, real pot, real tong, real ledger, labelled bots — because
 * v1 is online-only including vs-AI (PRD §9), and a zero-stakes practice mode
 * is explicitly backlog.
 */
export const CQueueSolo = z.object({
    t: z.literal('queue.solo'),
    game: GameId,
    tier: StakeTier,
    buyIn: Chips,
});
export const CTableLeave = z.object({ t: z.literal('table.leave'), tableId: z.string() });
/**
 * An engine action. The payload is opaque here and validated by the game's
 * own engine schema server-side — the wire type must not know the rules.
 */
export const CMatchAction = z.object({
    t: z.literal('match.action'),
    matchId: z.string(),
    /** Client's last seen event seq — lets the server reject stale actions. */
    seq: z.number().int().nonnegative(),
    action: z.unknown(),
});
export const CMatchResync = z.object({
    t: z.literal('match.resync'),
    matchId: z.string(),
    sinceSeq: z.number().int().nonnegative(),
});
export const CTableTalk = z.object({
    t: z.literal('table.talk'),
    tableId: z.string(),
    preset: TableTalkId,
});
/**
 * Sit at a *named* table — the one the player picked off the venue's list,
 * rather than whichever one matchmaking hands them. The buy-in is the table's
 * own; you cannot negotiate a stake into a room that already agreed one.
 */
export const CTableSit = z.object({ t: z.literal('table.sit'), tableId: z.string() });
/**
 * Fill the empty chairs at the table you are already sitting at with house bots,
 * now, instead of waiting for people who may never come.
 *
 * `queue.solo` opens a fresh vs-AI table; this rescues one you are already at —
 * a Kubo nobody joined, a table you picked off the venue list that stopped
 * filling, or one everybody else walked away from. Bots have only ever been
 * seated by matchmaking, so before this there was no way out of an empty
 * table except leaving it.
 */
export const CTableBots = z.object({ t: z.literal('table.bots'), tableId: z.string() });
export const CSpectateJoin = z.object({ t: z.literal('spectate.join'), tableId: z.string() });
export const CSpectateLeave = z.object({ t: z.literal('spectate.leave'), tableId: z.string() });
/**
 * A held-to-talk voice clip, relayed to the table like a talk preset.
 *
 * The payload is raw audio the CLIENTS agreed a shape for (16kHz mono µ-law
 * PCM, base64) — deliberately not an OS codec, because MediaRecorder's formats
 * do not round-trip across platforms: Android records webm/opus, which an
 * iPhone cannot decode. The server never decodes it; `format` travels so a
 * future client can change the shape without a protocol bump. Caps: 8 seconds
 * of speech, and a byte budget slightly above what that needs as base64.
 */
export const CVoiceClip = z.object({
    t: z.literal('voice.clip'),
    tableId: z.string(),
    format: z.string().max(80),
    data: z.string().min(1).max(360_000),
    /** Clip length the sender measured; receivers use it for the speaking badge. */
    ms: z.number().int().min(200).max(8_000),
});
export const CRematch = z.object({ t: z.literal('rematch'), tableId: z.string(), want: z.boolean() });
export const ClientMessage = z.discriminatedUnion('t', [
    CHello,
    CPing,
    CQueueJoin,
    CQueueLeave,
    CQueueSolo,
    CTableLeave,
    CMatchAction,
    CMatchResync,
    CTableTalk,
    CTableSit,
    CTableBots,
    CSpectateJoin,
    CSpectateLeave,
    CVoiceClip,
    CRematch,
]);
export const ERROR_CODES = [
    'unauthenticated',
    'age_gate_required',
    'insufficient_chips',
    'invalid_buyin',
    'illegal_action',
    'not_your_turn',
    'stale_seq',
    'table_full',
    'table_not_found',
    'match_not_found',
    'rate_limited',
    'kubo_tier_capped',
    'wallet_frozen',
    'internal',
];
//# sourceMappingURL=ws.js.map